/**
 * Single source of truth for Bearer access tokens on outbound HTTP (axios interceptors, fetch).
 * Resolution order matches authenticated Form Engine Portal flows:
 * AuthContext token provider → shared host/CaseMgmt token (sessionStorage) → MSAL silent after bootstrap.
 *
 * Does not dispatch logout or other side effects; callers own UX (e.g. api.service interceptor).
 * Keeps token logic out of api.service to avoid util → heavy service imports and circular graphs.
 */

import { AUTH_API_CONFIG } from '../config/api.config';
import { getSharedAccessToken } from './sharedAuth';
import { msalReady } from '../services/msalBootstrap.service';
import { tokenService } from '../services/token.service';
import { getValidAccessToken } from '../utils/tokenCache.utils';

let getBearerTokenFromContext: (() => Promise<string | null>) | undefined;

/**
 * Registers the optional token provider (e.g. from AuthContext / redirect handler).
 * Called by setupApiInterceptor; updates the shared reference without stacking interceptors.
 */
export function setBearerAccessTokenProvider(getToken?: () => Promise<string | null>): void {
  getBearerTokenFromContext = getToken;
}

/**
 * Resolves the current user's Bearer token for API and authenticated fetch calls.
 */
export async function resolveBearerAccessTokenForApiCalls(): Promise<string | null> {
  let token: string | null = null;
  if (getBearerTokenFromContext) {
    token = await getBearerTokenFromContext();
  }
  if (!token) {
    token = getSharedAccessToken();
  }
  // Non-MSAL mode can still receive a shared token via cross-portal URL/hash handoff.
  if (!token && AUTH_API_CONFIG.providerType !== 'msal') {
    return tokenService.getAccessToken();
  }
  if (!token) {
    const msalInstance = await msalReady;
    if (!msalInstance || msalInstance.getAllAccounts().length === 0) {
      return null;
    }
    token = await getValidAccessToken(msalInstance);
  }
  return token;
}
